<template>
  <div
    class="date-search"
    v-if="userStore.can('reports_edit') || userStore.can('writeoffs_edit')"
  >
    <h5 class="mb-2">
      <template v-if="reportType === 'acts_pp'">{{
        commissioning ? t('createCommissioningAct') : t('createWriteOffAct')
      }}</template>
      <template v-else-if="reportType">{{ t('createReport') }}</template>
    </h5>
    <div
      class="mb-3 col-xl-4 col-md-4 col-sm-4 d-flex align-items-center"
      v-if="reportType === 'acts_pp'"
    >
      <span class="mr-10">{{ t('basis') }}</span>
      <SelectIdObject
        :options="basisOptions"
        :autoSort="false"
        class="flex-grow-1 position-relative"
        style="z-index: 25"
        v-model="selectedBasis"
      />
    </div>

    <div
      class="mb-3 col-xl-4 col-md-4 col-sm-4 d-flex align-items-center"
      v-if="showActivitiesSelect"
    >
      <span class="mr-10">{{ t('activity') }}</span>
      <SelectIdObject
        :options="activitiesPrimary"
        class="flex-grow-1 position-relative"
        style="z-index: 22"
        v-model="selectedActivity"
      />
    </div>

    <SelectIdObject
      v-if="reportType === 'coordinator_po'"
      :options="forCoord"
      :autoSort="false"
      class="flex-grow-1 position-relative col-3 mb-2"
      style="z-index: 25"
      v-model="selectedForCoord"
    />

    <div class="mb-4 d-flex align-items-center justify-content-between">
      <div
        class="col-9 d-flex align-items-center"
        v-if="showDateSelect"
      >
        <div class="col-xl-4 col-md-4 col-sm-4 d-flex align-items-center">
          <span class="mr-10">{{ t('for') }}</span>
          <SelectIdObject
            :options="monthYears"
            :autoSort="false"
            class="flex-grow-1 position-relative"
            style="z-index: 20"
            v-model="selectedMonthYear"
          />
        </div>
        <span class="mx-3">{{ t('or') }}</span>
        <div class="col-xl-3 col-md-3 col-sm-3 d-flex align-items-center">
          <span class="mx-2">{{ t('from') }}</span>
          <DatePicker v-model="start_period" />
        </div>
        <div class="col-xl-3 col-md-3 col-sm-3 d-flex align-items-center">
          <span class="mx-2">{{ t('to') }}</span>
          <DatePicker
            v-model="end_period"
            :maxDate="current_date"
          />
        </div>
      </div>

      <div
        v-if="showOrdersSelect"
        class="col-9 d-flex align-items-center"
      >
        <span class="mr-10">{{ t('byRequest') }}</span>
        <SelectIdObject
          :options="orders.data"
          :autoSort="false"
          class="flex-grow-1 position-relative"
          style="z-index: 10"
          v-model="selectedOrder"
        />
      </div>

      <div
        v-if="showDestructionsSelect"
        class="col-9 d-flex align-items-center"
      >
        <span class="mr-10">{{ t('byDestruction') }}</span>
        <SelectIdObject
          :options="destructions.data"
          :autoSort="false"
          class="flex-grow-1 position-relative"
          style="z-index: 10"
          v-model="selectedDestruction"
        />
      </div>

      <button
        type="button"
        @click.prevent="createReport"
        class="btn btn-secondary"
      >
        <div class="text-nowrap">{{ t('generate') }}</div>
      </button>
    </div>
  </div>
</template>

<script setup>
import SelectIdObject from '@/components/select/selectIdObject.vue'
import DatePicker from '@/components/datepicker.vue'
import { computed, onMounted, ref, watch } from 'vue'
import { format } from 'date-fns'
import { useUserStore } from '@/stores/user.js'
import { useApi } from '@/composables/api'
import { storeToRefs } from 'pinia'
import { useActivityStore } from '@/stores/activity.js'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const userStore = useUserStore()
const { activityTypes } = storeToRefs(useActivityStore())

const selectedOrder = ref(null)
const selectedDestruction = ref(null)
const selectedActivity = ref(null)
const activitiesPrimary = ref([])

// Функция для обновления обоих значений
function updateActivities() {
  const filtered = activityTypes.value.filter(a => a.parent_id === null) ?? []
  activitiesPrimary.value = filtered

  if (filtered.length > 0 && !selectedActivity.value) {
    selectedActivity.value = filtered[0]
  }
}

watch(activityTypes, updateActivities, { immediate: true })

const emit = defineEmits(['create_report'])

const props = defineProps({
  date: {
    type: Boolean,
    default: false,
  },
  reportType: {
    type: String,
    default: '',
  },
  commissioning: {
    type: Boolean,
    default: false,
  },
})

const currentDate = new Date()
const currentYear = currentDate.getFullYear()
const currentMonth = currentDate.getMonth()

const months = Array.from({ length: 12 }, (_, month) => t(`months.${month}`))
const forCoord = [
  { id: 0, name: t('byInvoiceDate') },
  { id: 1, name: t('byPoDate') },
]
const basisOptions = computed(() => {
  const options = [
    { id: 'period', name: t('basisPeriod') },
    { id: 'order', name: t('byRequest') },
    { id: 'activity', name: t('byActivity') },
  ]
  if (!props.commissioning) {
    options.push({ id: 'destruction', name: t('byDestruction') })
  }
  return options
})
const selectedForCoord = ref(forCoord[0])
const selectedBasis = ref(basisOptions.value[0])
watch(basisOptions, options => {
  if (!options.some(option => option.id === selectedBasis.value?.id)) {
    selectedBasis.value = options[0]
  }
})
const showDateSelect = computed(() => {
  if (props.reportType !== 'acts_pp') return true
  return selectedBasis.value.id !== 'order' && selectedBasis.value.id !== 'destruction'
})
const showActivitiesSelect = computed(() => {
  return props.reportType === 'acts_pp' && selectedBasis.value.id === 'activity'
})
const showOrdersSelect = computed(() => {
  return props.reportType === 'acts_pp' && selectedBasis.value.id === 'order'
})
const showDestructionsSelect = computed(() => {
  return props.reportType === 'acts_pp' && selectedBasis.value.id === 'destruction'
})

const generateMonthYears = () => {
  const startYear = 2024
  const monthYears = [{ id: 'custom', name: t('customValue') }]

  for (let year = currentYear; year >= startYear; year--) {
    const endMonth = year === currentYear ? currentMonth : 11
    const startMonth = 0

    for (let month = endMonth; month >= startMonth; month--) {
      monthYears.push({
        id: `${month}-${year}`,
        name: `${months[month]} ${year}`,
      })
    }
  }
  return monthYears
}

const monthYears = generateMonthYears()

const selectedMonthYear = ref({
  id: `${currentMonth}-${currentYear}`,
  name: `${months[currentMonth]} ${currentYear}`,
})
const start_period = ref(new Date(currentYear, currentMonth, 1))
const end_period = ref(new Date())
const current_date = ref(new Date())

const updatePeriods = () => {
  if (selectedMonthYear.value.id !== 'custom') {
    const [month, year] = selectedMonthYear.value.id.split('-').map(Number)
    start_period.value = new Date(year, month, 1)
    if (current_date.value < end_period.value) {
      end_period.value = current_date.value
    } else {
      end_period.value = new Date(year, month + 1, 0)
    }
  }
}

const changePeriod = () => {
  const start = new Date(start_period.value)
  const end = new Date(end_period.value)

  if (current_date.value < end_period.value) {
    end_period.value = current_date.value
  }

  const startMonth = start.getMonth()
  const endMonth = end.getMonth()
  const startYear = start.getFullYear()
  const endYear = end.getFullYear()

  const isFullMonth =
    start.getDate() === 1 &&
    end.getDate() === new Date(startYear, startMonth + 1, 0).getDate() &&
    startMonth === endMonth &&
    startYear === endYear

  const isCurrentMonthPeriod =
    startMonth === currentMonth &&
    startYear === currentYear &&
    start.getDate() === 1 &&
    end.getDate() === currentDate.getDate()

  selectedMonthYear.value =
    isFullMonth || isCurrentMonthPeriod
      ? monthYears.find(option => option.id === `${startMonth}-${startYear}`)
      : { id: 'custom', name: t('customValue') }
}

const createReport = () => {
  let data = {}
  if (showDateSelect.value) {
    data.date_from = format(start_period.value, 'yyyy-MM-dd')
    data.date_to = format(end_period.value, 'yyyy-MM-dd')
    data.options = {}
  }
  if (selectedForCoord.value.id === 1) {
    data.options = { po_date: true }
  }
  if (showOrdersSelect.value && selectedOrder.value) {
    data.options = { order: true }
    data.order = selectedOrder.value
  }
  if (showDestructionsSelect.value && selectedDestruction.value) {
    data.options = { destruction: true }
    data.destruction = selectedDestruction.value
  }
  if (showActivitiesSelect.value && selectedActivity.value) {
    data.options = { activity_type: true }
    data.activity_type = selectedActivity.value
  }

  if (data.options) emit('create_report', data)
}

watch(selectedMonthYear, () => {
  updatePeriods()
})

watch([start_period, end_period], () => {
  changePeriod()
})

const kindQuery = computed(() => ({ commissioning: props.commissioning }))

const { model: orders, refresh: refreshOrders } = useApi({
  url: 'orders_list',
  query: kindQuery,
  data: { data: [] },
})

const { model: destructions, refresh: refreshDestructions } = useApi({
  url: 'destructions_list',
  query: kindQuery,
  data: { data: [] },
})

onMounted(() => {
  if (userStore.can(['reports_edit']) || userStore.can(['writeoffs_edit'])) {
    refreshOrders()
    refreshDestructions()
  }
})
</script>

<i18n lang="json">
{
  "ru": {
    "createWriteOffAct": "Сформировать новый акт списания ТМЦ",
    "createCommissioningAct": "Сформировать новый акт ввода в эксплуатацию",
    "createReport": "Сформировать новый отчет",
    "basis": "Основание",
    "activity": "Активность",
    "for": "за",
    "or": "или",
    "from": "с",
    "to": "до",
    "byRequest": "По заявке",
    "byDestruction": "По уничтожению",
    "generate": "Сформировать",
    "months": ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"],
    "byInvoiceDate": "Сформировать по дате инвойса",
    "byPoDate": "Сформировать по дате РО",
    "basisPeriod": "За период",
    "byActivity": "По активности",
    "customValue": "Индивидуальное значение"
  },
  "en": {
    "createWriteOffAct": "Generate a new inventory write-off act",
    "createCommissioningAct": "Generate a new commissioning act",
    "createReport": "Generate a new report",
    "basis": "Basis",
    "activity": "Activity",
    "for": "for",
    "or": "or",
    "from": "from",
    "to": "to",
    "byRequest": "By request",
    "byDestruction": "By destruction",
    "generate": "Generate",
    "months": ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
    "byInvoiceDate": "Generate by invoice date",
    "byPoDate": "Generate by PO date",
    "basisPeriod": "For the period",
    "byActivity": "By activity",
    "customValue": "Custom value"
  }
}
</i18n>
